import React, { useState, useEffect, useRef } from 'react'
import { useMediaQuery } from 'react-responsive'
import api from '../../services/api'
import { useSelector, useDispatch } from 'react-redux'

import { SendOutlined } from '@ant-design/icons'
import { Drawer, Input, Spin } from 'antd'
import { errorMessage } from '~/services/messager'
import arrow_up_icon from '../../assets/icons/arrow_up_icon.svg'
import assistant_icon from '../../assets/icons/assistant_icon.svg'
import user_icon from '../../assets/icons/user_icon.svg'
import Showdown from 'showdown'

import {
	AssistantMessage,
	CopyToCKEditorButton,
	InputContainer,
	Messages,
	SendButton,
	SendContainer,
	UserMessage,
} from './styles'

import { array, bool, func, string } from 'prop-types'
import {
	uploadThreadId_DocumentDetail,
	uploadThreadId_DocumentForm,
} from '~/states/modules/contextAi'

const VirtualAssistant = ({
	open,
	onClose,
	exportText,
	loadingPreviousMessages = false,
	previousMessages = null,
	doc_type = '',
	prompt = '',
	promptResponse = '',
}) => {
	const converter = new Showdown.Converter()
	const isMobile = useMediaQuery({ query: '(max-width: 768px)' })
	const assistantWidth = isMobile ? '100%' : '50%'

	const { Context, documentDetail, documentForm } = useSelector(
		({ contextAi }) => contextAi
	)
	const doc_id = useSelector(({ documentDetail }) => documentDetail.data.id)
	const dispatch = useDispatch()

	const loadPreviousMessages = loadingPreviousMessages

	const [loading, setLoading] = useState(false)
	const [inputValue, setInputValue] = useState('')
	const [messages, setMessages] = useState([
		{
			role: 'assistant',
			content:
				'Olá, me pergunte o que quiser, e se quiser acrescentar uma resposta ao texto click na setinha da mensagem',
		},
	])

	const messagesEndRef = useRef(null)

	// Use effect to update messages
	useEffect(() => {
		if (prompt !== '' && promptResponse !== '') {
			setMessages([
				{
					role: 'user',
					content: prompt,
				},
				{
					role: 'assistant',
					content: promptResponse,
				},
			])
		} else if (previousMessages !== null) {
			setMessages(previousMessages)
		}
	}, [prompt, promptResponse, previousMessages])

	useEffect(() => {
		// Scroll when update messages
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
		}
	}, [messages])

	const sendMessage = () => {
		if (inputValue === '') return
		let payload
		if (
			doc_type === 'documentDetail' &&
			Object.keys(documentDetail).includes(`${doc_id}`) &&
			Context !== null
		) {
			payload = {
				messages: [...messages, { role: 'user', content: inputValue }],
				context_file_name: `${Context}`,
				thread_id: documentDetail[`${doc_id}`],
			}
		} else if (
			doc_type === 'documentForm' &&
			Object.keys(documentForm).includes(`${Context}`) &&
			Context !== null
		) {
			payload = {
				messages: [...messages, { role: 'user', content: inputValue }],
				context_file_name: `${Context}`,
				thread_id: documentForm[`${Context}`],
			}
		} else if (Context === null) {
			payload = {
				messages: [...messages, { role: 'user', content: inputValue }],
			}
		} else {
			payload = {
				messages: [...messages, { role: 'user', content: inputValue }],
				context_file_name: `${Context}`,
			}
		}
		setLoading(true)
		api
			.post('/ai/', payload)
			.then((response) => {
				setLoading(false)
				const data = response.data
				const newMessages = [
					...messages,
					{ role: 'user', content: inputValue },
					{ role: 'assistant', content: data.answer },
				]
				if (
					doc_type === 'documentDetail' &&
					!Object.values(documentDetail).includes(data.thread_id) &&
					data.thread_id !== ''
				) {
					const newKey = `${doc_id}`
					dispatch(uploadThreadId_DocumentDetail({ [newKey]: data.thread_id }))
				} else if (
					doc_type === 'documentForm' &&
					!Object.values(documentForm).includes(data.thread_id) &&
					data.thread_id !== ''
				) {
					const newKey = `${Context}`
					dispatch(uploadThreadId_DocumentForm({ [newKey]: data.thread_id }))
				}
				setMessages(newMessages)
				setInputValue('')
			})
			.catch((error) => {
				setLoading(false)
				errorMessage({
					content:
						'Houve um erro na comunicação com o ChatGPT. \n Por favor, tente novamente mais tarde.',
				})
			})
	}

	const handleInputChange = (event) => {
		setInputValue(event.target.value)
	}

	return (
		<Drawer
			title="Assistente Virtual"
			placement="right"
			open={open}
			zIndex={50}
			onClose={onClose}
			width={assistantWidth}
			bodyStyle={{ padding: 10, display: 'flex', flexDirection: 'column' }}
			headerStyle={{
				padding: '0 50 0 50',
			}}>
			<Messages>
				{messages.map((message, index) =>
					message.role === 'user' ? (
						<UserMessage key={index}>
							<img src={user_icon} alt="User icon" />
							{loadPreviousMessages ? <Spin /> : <span>{message.content}</span>}
						</UserMessage>
					) : (
						<AssistantMessage key={index + 'assistant'}>
							<span>
								{doc_type === 'documentForm' && (
									<CopyToCKEditorButton
										onClick={() => {
											exportText(message.content)
											onClose()
										}}>
										<img
											src={arrow_up_icon}
											alt="Arrow up icon"
											style={{ transform: 'rotate(-90deg)' }}
										/>
									</CopyToCKEditorButton>
								)}
								{loadPreviousMessages ? (
									<Spin />
								) : (
									<span
										dangerouslySetInnerHTML={{
											__html: converter.makeHtml(message.content),
										}}
									/>
								)}
							</span>
							<img src={assistant_icon} alt="Assitant icon" />
						</AssistantMessage>
					)
				)}
				<div ref={messagesEndRef} />
			</Messages>

			<InputContainer>
				<Input
					type="text"
					value={inputValue}
					onChange={handleInputChange}
					placeholder="Digite seu prompt"
					onPressEnter={sendMessage}
					disabled={loading}
				/>
				<SendContainer>
					{loading || loadPreviousMessages ? (
						<Spin />
					) : (
						<SendButton onClick={sendMessage} icon={<SendOutlined />} />
					)}
				</SendContainer>
			</InputContainer>
		</Drawer>
	)
}

VirtualAssistant.propTypes = {
	open: bool.isRequired,
	onClose: func.isRequired,
	exportText: func.isRequired,
	loadingPreviousMessages: bool,
	previousMessages: array,
	doc_type: string,
	prompt: string,
	promptResponse: string,
}

export default VirtualAssistant
